// CopyButton — copy-to-clipboard for a value the UI can only show
// truncated (a tx hash, a deposit address, an order id).
//
// Lifted out of WithdrawPanel for SM-335: the on-chain treasury card
// needs the same affordance, and an operator standing at a hardware
// wallet must never be asked to transcribe an address from a tooltip.
//
// Reverts to the idle icon after a beat so a second copy is obviously
// available; failure (a browser that refuses clipboard access, or a
// non-secure origin where navigator.clipboard is undefined) leaves the
// button silent rather than claiming success.
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/cn";

export interface CopyButtonProps {
  value: string;
  /** Noun for the aria-label, e.g. "deposit address". */
  label: string;
  className?: string;
}

export function CopyButton({ value, label, className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  // Clear the timer on unmount — the treasury card swaps panes and
  // networks under the operator, and a setState after unmount is a
  // React warning nobody will chase down later.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <button
      type="button"
      aria-label={copied ? `${label} copied` : `Copy ${label}`}
      title={copied ? "Copied" : "Copy full value"}
      className={cn(
        "rounded p-0.5 text-2xs transition-colors",
        copied ? "text-success" : "text-muted hover:text-fg",
        className,
      )}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => undefined);
      }}
    >
      {copied ? "✓" : "⧉"}
    </button>
  );
}
